import { ChevronRight } from "lucide-react";
import { Fragment, type ReactNode, useMemo } from "react";
import { cn } from "@/v2/lib/utils";
import { BillableIcon } from "./BillableIcon";
import type { Entry } from "./normalize";
import { getStatusMeta, StatusIcon } from "./StatusIcon";

function fmt(h: number): string {
	return `${h.toFixed(2)} h`;
}

interface Totals {
	tracked: number;
	reviewed: number;
	exported: number;
	billable: number;
	nonBillable: number;
	billableCredit: number;
	unknown: number;
	total: number;
}

function computeTotals(entries: Entry[]): Totals {
	let tracked = 0;
	let reviewed = 0;
	let exported = 0;
	let billable = 0;
	let nonBillable = 0;
	let billableCredit = 0;
	let unknown = 0;
	for (const e of entries) {
		if (e.status === "tracked") tracked += e.units;
		if (e.status === "reviewed") reviewed += e.units;
		if (e.status === "exported") exported += e.units;
		// Deleted entries are intentionally omitted from every total.
		if (e.status === "deleted") continue;
		if (e.billableCategory === "billable") billable += e.units;
		if (e.billableCategory === "non_billable") nonBillable += e.units;
		if (e.billableCategory === "billable_credit") billableCredit += e.units;
		if (e.billableCategory === "unknown") unknown += e.units;
	}
	return {
		tracked,
		reviewed,
		exported,
		billable,
		nonBillable,
		billableCredit,
		unknown,
		total: tracked + reviewed + exported,
	};
}

interface StatInlineProps {
	label: string;
	hours: number;
	icon?: ReactNode;
	className?: string;
}

// Single inline stat shared by both distribution groups, so font size and
// orientation stay identical across the left and right halves of the bar.
function StatInline({ label, hours, icon, className }: StatInlineProps) {
	return (
		<div
			className={cn(
				"flex min-w-max shrink-0 items-center gap-1.5 whitespace-nowrap [&_svg]:size-3.5",
				className,
			)}
		>
			{icon}
			<span className="text-xs text-muted-foreground">{label}</span>
			<span className="text-sm font-medium tabular-nums text-foreground">
				{fmt(hours)}
			</span>
		</div>
	);
}

function Divider({ className }: { className?: string }) {
	return <div className={cn("h-8 w-px shrink-0 bg-border", className)} />;
}

// The Total is the right-hand anchor, so it stacks vertically to read as a
// summary rather than another inline category.
function TotalStat({ hours }: { hours: number }) {
	return (
		<div className="flex h-full w-max shrink-0 flex-col items-end justify-center gap-0 whitespace-nowrap @6xl:h-auto">
			<span className="text-base font-semibold leading-tight tabular-nums text-foreground">
				{fmt(hours)}
			</span>
			<span className="text-xs text-muted-foreground">Total</span>
		</div>
	);
}

// Pipeline stages, in workflow order.
type PipelineStatus = "tracked" | "reviewed" | "exported";
const PIPELINE_ORDER: readonly PipelineStatus[] = [
	"tracked",
	"reviewed",
	"exported",
];
const PIPELINE_SEGMENT_BG: Record<PipelineStatus, string> = {
	tracked: "bg-status-tracked",
	reviewed: "bg-status-reviewed",
	exported: "bg-status-exported",
};

// Billable mix, in the same left-to-right order as the stats above the bar.
// `unknown` is the only non-billable-token color collision (it shares
// muted-foreground with non-billable), so it's faded to read as "uncategorized"
// and stay distinct from the solid non-billable segment.
const BILLABLE_SEGMENT_BG = {
	billable: "bg-billable",
	billable_credit: "bg-billable-credit",
	non_billable: "bg-muted-foreground",
	unknown: "bg-muted-foreground/30",
} as const;

interface BarSegment {
	key: string;
	value: number;
	className: string;
}

// A stacked distribution bar.
function DistributionBar({ segments }: { segments: BarSegment[] }) {
	const total = segments.reduce((sum, s) => sum + s.value, 0);
	return (
		<div
			className="flex h-1.5 w-full overflow-hidden rounded-full bg-border"
			aria-hidden
		>
			{segments.map((s) => {
				const pct = total > 0 ? (s.value / total) * 100 : 0;
				return (
					<div
						key={s.key}
						className={s.className}
						style={{ width: `${pct}%` }}
					/>
				);
			})}
		</div>
	);
}

interface StatsBarProps {
	entries: Entry[];
}

export function StatsBar({ entries }: StatsBarProps) {
	// StatsBar is static for the current entry scope and does not react to row
	// selection.
	const totals = useMemo(() => computeTotals(entries), [entries]);

	const pipelineSegments: BarSegment[] = PIPELINE_ORDER.map((status) => ({
		key: status,
		value: totals[status],
		className: PIPELINE_SEGMENT_BG[status],
	}));
	const billableSegments: BarSegment[] = [
		{
			key: "billable",
			value: totals.billable,
			className: BILLABLE_SEGMENT_BG.billable,
		},
		{
			key: "billable_credit",
			value: totals.billableCredit,
			className: BILLABLE_SEGMENT_BG.billable_credit,
		},
		{
			key: "non_billable",
			value: totals.nonBillable,
			className: BILLABLE_SEGMENT_BG.non_billable,
		},
		{
			key: "unknown",
			value: totals.unknown,
			className: BILLABLE_SEGMENT_BG.unknown,
		},
	];

	// Wide containers keep the existing one-line layout. Below that breakpoint,
	// keep Total anchored on the right and let it span both distribution rows;
	// the left column fills the remaining width, so both bars extend to Total and
	// stay the same length. Label rows are intentionally non-wrapping; if the
	// viewport is too tight, the outer wrapper scrolls horizontally.
	return (
		<div className="@container overflow-x-auto border-t border-border bg-muted/40 px-4 py-2">
			<div className="grid w-full min-w-max grid-cols-[minmax(max-content,1fr)_auto] grid-rows-[auto_auto] items-stretch gap-x-4 gap-y-2 @6xl:flex @6xl:min-h-[3.5rem] @6xl:items-center @6xl:gap-3">
				<div className="flex min-w-0 flex-col gap-1.5 @6xl:w-max @6xl:shrink-0">
					<div className="flex w-max flex-nowrap items-center gap-x-2">
						{PIPELINE_ORDER.map((status, i) => (
							<Fragment key={status}>
								{i > 0 && (
									<ChevronRight className="size-3.5 shrink-0 text-muted-foreground/40" />
								)}
								<StatInline
									label={getStatusMeta(status).label}
									hours={totals[status]}
									icon={<StatusIcon status={status} className="size-3.5" />}
								/>
							</Fragment>
						))}
					</div>
					<DistributionBar segments={pipelineSegments} />
				</div>

				<div className="flex min-w-0 flex-col gap-1.5 @6xl:ml-auto @6xl:w-max @6xl:shrink-0">
					<div className="flex w-max flex-nowrap items-center gap-x-7">
						<StatInline
							label="Billable"
							hours={totals.billable}
							icon={<BillableIcon category="billable" className="size-3.5" />}
						/>
						<StatInline
							label="Credit"
							hours={totals.billableCredit}
							icon={
								<BillableIcon category="billable_credit" className="size-3.5" />
							}
						/>
						<StatInline
							label="Non-Billable"
							hours={totals.nonBillable}
							icon={
								<BillableIcon category="non_billable" className="size-3.5" />
							}
						/>
						<StatInline
							label="Unknown"
							hours={totals.unknown}
							icon={<BillableIcon category="unknown" className="size-3.5" />}
						/>
					</div>
					<DistributionBar segments={billableSegments} />
				</div>

				<div className="col-start-2 row-span-2 row-start-1 flex items-stretch gap-4 @6xl:contents">
					<Divider className="h-auto self-stretch @6xl:h-8 @6xl:self-center" />
					<TotalStat hours={totals.total} />
				</div>
			</div>
		</div>
	);
}
